import { AfterViewInit, Component, ComponentFactoryResolver, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AuthenticationResult } from '../../../shared/models/authentication-result';
import { JobsStore } from '../../../jobs/store/jobs-store.service';
import { AppStore } from '../../../shared/store/lookup-store.service';
import { UiStateStore } from '../../../shared/store/ui-state-store.service';
import { DataService } from '../../data.service';
import { JobFilter } from '../../models/job-filter';
import { JobSummary } from '../../models/job-summary';
import { SortOrders } from '../../../shared/models/sort-orders';
import { Navigation } from '../../../shared/models/navigation';
import { AccountTypes } from '../../../shared/models/AccountTypes';
import { WorkTypes } from '../../../shared/enums/work-types';
import { LocationTypes } from '../../../shared/constants/location-types';

@Component({
  selector: 'app-search-jobs',
  templateUrl: './search-jobs.component.html',
  styleUrls: ['./search-jobs.component.scss']
})
export class SearchJobsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren('dateposted') postedDateControls: QueryList<Input>;
  @ViewChildren('jobsector') sectorControls: QueryList<Input>;
  @ViewChildren('jobType') jobTypeControls: QueryList<Input>;
  @ViewChildren('experience') experienceControls: QueryList<Input>;
  @ViewChildren('salary') salariesControls: QueryList<Input>;
  @ViewChildren('qualification') qualificationControls: QueryList<Input>;

  jobKeyword: string;
  location: string;
  _pageCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  pageCount: Observable<number> = this._pageCount.asObservable();
  showPager: boolean = false;
  showFilteringJobs: boolean = false;
  jobs: JobSummary[] = [];
  annualSalaries: number[] = [10000, 20000, 30000, 40000, 50000, 60000];
  dailySalaries: number[] = [100, 200, 300, 400, 500, 600];
  hourlySalaries: number[] = [10, 20, 30, 40, 50, 60];
  navigations: Navigation[] = [];
  selectedExperiences: number[] = [];
  selectedDatePosted: number[] = [];
  jobFilter: JobFilter;
  user: AuthenticationResult = null;
  subscriptions: Subscription[] = [];
  currentRoute: string;
  workTypes = WorkTypes;
  workRemote: boolean = false;
  workOnSite: boolean = false;
  locationTypes = LocationTypes;
  active = 1;

  constructor(
    public jobStore: JobsStore,
    public lookupStore: AppStore,
    public uiStore: UiStateStore,
    public dataService: DataService,
    public router: Router,
    public modalService: NgbModal,
    public route: ActivatedRoute,
    public resolver: ComponentFactoryResolver) {
    this.currentRoute = this.route.snapshot.url[0].path;
  }

  ngOnInit() {
    let userSubscription = this.uiStore.authenticatedUser.subscribe(user => {
      if (!user) {
        return;
      }

      this.user = user;
      this.navigations = this.user.accountType == AccountTypes.Candidate ? Navigation.getCandidateNavigations() : Navigation.getEmployerNavigations();
    });

    this.subscriptions.push(userSubscription);

    let filterSubscription = this.jobStore.jobSearchFilter.subscribe(filter => {
      if (!filter) {
        return;
      }

      this.jobFilter = filter;
      if(this.jobFilter.keyword) {
        this.showFilteringJobs = true;
      }
    });

    this.subscriptions.push(filterSubscription);

    let resultSubscription = this.jobStore.jobSearchResults.subscribe(searchResult => {
      if (!searchResult) {
        return;
      }

      this.showFilteringJobs = false;
      this.jobs = [];
      this.showPager = searchResult.pageCount > 1;
      this._pageCount.next(searchResult.pageCount);
      searchResult.result.forEach(j => {
        this.jobs.push(j);
      });
    });

    this.subscriptions.push(resultSubscription);
  }

  ngAfterViewInit() {
    if (!this.jobFilter) {
      return;
    }

    if (this.jobFilter.datePostedPeriod) {
      let postedDateControl = this.postedDateControls.find(ctrl => (ctrl as ElementRef).nativeElement.id == `dateposted-${this.jobFilter.datePostedPeriod}`);
      (postedDateControl as ElementRef).nativeElement.checked = true;
    }

    if (this.jobFilter.sectors.length > 0) {
      this.jobFilter.sectors.forEach(sectorId => {
        let sectorControl = this.sectorControls.find(ctrl => (ctrl as ElementRef).nativeElement.id == `sector-${sectorId}`);
        (sectorControl as ElementRef).nativeElement.checked = true;
      });
    }

    if (this.jobFilter.jobTypes.length > 0) {
      this.jobFilter.jobTypes.forEach(jobTypeId => {
        let jobTypeControl = this.jobTypeControls.find(ctrl => (ctrl as ElementRef).nativeElement.id == `jobType-${jobTypeId}`);
        (jobTypeControl as ElementRef).nativeElement.checked = true;
      });
    }

    if (this.jobFilter.experience) {
      let experienceControl = this.experienceControls.find(ctrl => (ctrl as ElementRef).nativeElement.id == `experiece-${this.jobFilter.experience}`);
      (experienceControl as ElementRef).nativeElement.checked = true;
    }

    if (this.jobFilter.minimumSalary) {
      let salaryControl = this.salariesControls.find(ctrl => (ctrl as ElementRef).nativeElement.id == `salary-${this.jobFilter.minimumSalary}`);
      (salaryControl as ElementRef).nativeElement.checked = true;
    }

    if (this.jobFilter.qualifications) {
      this.jobFilter.qualifications.forEach(qualification => {
        let qualificationControl = this.qualificationControls.find(ctrl => (ctrl as ElementRef).nativeElement.id == `educationLevel-${qualification}`);
        (qualificationControl as ElementRef).nativeElement.checked = true;
      });
    }

  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onSortOrderChanged(event): void {
    this.jobFilter.sortBy = Number.parseInt(event.target.value);
    this.filterJobs();
  }

  onDatePostedPeriodClicked(event, period: number): void {
    if (event.target.checked) {
      this.selectedDatePosted.push(period);
    } else {
      this.selectedDatePosted = this.selectedDatePosted.filter(x => x != period);
    }

    this.jobFilter.datePostedPeriod = Math.max(...this.selectedDatePosted);
    this.filterJobs();
  }

  onSectorClicked(event, id: string): void {
    if (event.target.checked) {
      this.jobFilter.sectors.push(id);
    } else {
      this.jobFilter.sectors = this.jobFilter.sectors.filter(x => x != id);
    }

    this.filterJobs();
  }

  onJobTypeClicked(event, id: string): void {
    if (event.target.checked) {
      this.jobFilter.jobTypes.push(id);
    } else {
      this.jobFilter.jobTypes = this.jobFilter.jobTypes.filter(x => x != id);
    }

    this.filterJobs();
  }

  onExperienceClicked(event, years: number): void {
    if (event.target.checked) {
      this.selectedExperiences.push(years);
    } else {
      this.selectedExperiences = this.selectedExperiences.filter(x => x != years);
    }

    this.jobFilter.experience = Math.min(...this.selectedExperiences);
    this.filterJobs();
  }

  onEducationLevelClicked(event, id: string): void {
    if (event.target.checked) {
      this.jobFilter.qualifications.push(id);
    } else {
      this.jobFilter.qualifications = this.jobFilter.qualifications.filter(x => x != id);
    }

    this.filterJobs();
  }

  handleLocationSearch = () => {
    this.filterJobs();
  }

  handleWorkTypeFilter = (value: number) => {
    this.jobFilter.locationFilter.workType = null;
    if(this.workOnSite && !this.workRemote) {
      this.jobFilter.locationFilter.workType = 1;
    } else if(!this.workOnSite && this.workRemote) {
      this.jobFilter.locationFilter.workType = 0;
    }

    this.filterJobs();
  }

  onSalaryRangeClicked(event, salaryTypeValue: string, minimum: number): void {
    if (event.target.checked) {
      let salaryType = this.lookupStore.getSalaryTypeByName(salaryTypeValue);
      this.jobFilter.salaryTypeId = salaryType.id;
      this.jobFilter.minimumSalary = minimum;

      this.filterJobs();
      return;
    }

    this.jobFilter.minimumSalary = null;
    this.filterJobs();
  }

  onPagerValueChanged(event): void {
    this.jobFilter.pageNumber = event.number;
    this.filterJobs();
  }

  onJobTitleClicked(jobId: string): void {
    this.router.navigate([`/view-job`, jobId, this.currentRoute]);
    this.uiStore.setSelectedJobId(jobId);
  }

  onFavouriteClicked(jobId: string): void {
    let foundJob = this.jobs.find(job => job.id == jobId);
    if (!foundJob) {
      throw new Error(`Job not found for Id ${jobId}`);
    }

    foundJob.bookmarked = !foundJob.bookmarked;
    if (foundJob.bookmarked) {
      this.jobStore.bookmarkJobInLocalStore(foundJob.id);
    } else {
      this.jobStore.unBookmarkJobInLocalStore(foundJob.id);
    }
  }

  clearFilter(): void {
    this.jobFilter = new JobFilter();
    this.filterJobs();
  }

  filterJobs(): void {
    this.jobStore.loadSearchResults(this.jobFilter);
  }

}
